import React, { useState, useEffect } from 'react';
import { AchievementSystem } from '../../components/user/AchievementSystem';
import { useAuth } from '../../hooks/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';

export const Achievements: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasAccessCode, setHasAccessCode] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user has access code
  useEffect(() => {
    const checkAccessCode = async () => {
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accessCodeId) {
            // User has access code, verify it's still valid
            const accessCodeDoc = await getDoc(doc(db, 'accessCodes', userData.accessCodeId));
            if (accessCodeDoc.exists()) {
              const accessCodeData = accessCodeDoc.data();
              if (accessCodeData.isActive && 
                  (!accessCodeData.expiresAt || accessCodeData.expiresAt.toDate() > new Date())) {
                setHasAccessCode(true);
              } else {
                setHasAccessCode(false);
              }
            } else {
              setHasAccessCode(false);
            }
          } else {
            setHasAccessCode(false);
          }
        } else {
          setHasAccessCode(false);
        }
      } catch (error) {
        console.error('Error checking access code:', error);
        setHasAccessCode(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccessCode();
  }, [user]);

  // Check if user has access code - redirect if not
  if (hasAccessCode === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Premium Achievements Access Required
          </h2>
          <p className="text-gray-600 mb-6">
            Achievements and progress tracking are available to users with department access codes. 
            Contact your department administrator to get your access code.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/training-access')}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <Shield className="w-5 h-5" />
              <span>Enter Access Code</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Achievements</h1>
          <p className="text-gray-600">
            Track your learning progress and unlock badges as you complete modules and quizzes.
          </p>
        </div>
        
        <AchievementSystem />
      </div>
    </div>
  );
};
